import { flatten, pick } from 'lodash-es';
import { eachDayOfInterval, isSameDay } from 'date-fns';
import {
  VacationApplicationInput,
  VacationScheduleInput,
  VacationScheduleItemType,
  VacationScheduleReplaceInput,
  VacationApplicationType,
  VacationScheduleType,
  VacationStatusesEnum,
  Scalars
} from '../../graphql/types';
import { convertToUtcStr, getDateUtc } from '../../utils/date/date';
import { UploadFileType } from '../../common/components/ui/FileLoader/FileLoader';
import {
  vacationChildKinshipDegreeSelectOptions,
  vacationReasonSelectOptions,
  vacationTypeSelectOptions
} from '../../utils/form/vacationSelectOptions';
import {
  VACATION_REASON_WITHOUT_PAY_OTHER,
  VACATION_TYPE_BLOOD_DONATION,
  VACATION_TYPE_CHILD_CARE,
  VACATION_TYPE_DISABLED_CHILD_CARE,
  VACATION_TYPE_PREGNANCY,
  VACATION_TYPE_STUDY_WITHOUT_PAY,
  VACATION_TYPE_STUDY_WITH_PAY,
  VACATION_TYPE_WITHOUT_PAY
} from '../../routes/vacations/index/VacationAppForm/VacationAppForm';

export interface VacationAppProps extends Partial<VacationApplicationInput> {
  availableDaysAmount?: number | null;
  selectedDaysAmount?: number | null;
  isHeadPosition: boolean;
  deputy_position_id?: string;
  vacation_type: string; // Вид отпуска
  // Без сохранения заработной платы
  no_pay_reason: string; // причина отпуска из списка
  no_pay_reason_text: string; // причина отпуска ручной ввод
  // Учебный
  invitation_note: UploadFileType[] | undefined; // Справка-вызов (Файл)
  // По беременности и родам
  digital_sick_leave_issued: boolean; // Выдан электронный больничный
  sick_leave_number: string | null; // Номер электронного больничного
  sick_leave_doc: UploadFileType[] | undefined; // Больничный (Файл)
  // По уходу за ребенком до 3-х лет
  birth_certificate: UploadFileType[] | undefined; // Свидетельство о рождении ребенка (Файл)
  date_of_birth: Date | null;

  // По уходу за детьми-инвалидами
  disability_certificate: UploadFileType[] | undefined; // Справка об инвалидности
  disability_permanent: boolean; // Бессрочная инвалидность
  disability_start: Date | null;
  disability_end: Date | null;
  confirm_child_place: UploadFileType[] | undefined;
  child_surname: string;
  child_name: string;
  child_patronymic: string;
  child_birth_date: Date | null;
  child_snils: string;
  child_kinship_degree: string;
  disabled_child_vacation_periods: ChildVacationPeriod[] | undefined;
  // Справка с места работы другого родителя о том,
  // что дополнительные оплачиваемые выходные дни не использованы или документ,
  // подтверждающий факт смерти другого родителя.
  cert_other_parent_place: UploadFileType[] | undefined;
  // Справка с предыдущего места работы заявителя о том,
  // что на дату увольнения доп.выходные дни не использованы или
  // использованы частично с указанием дат и общего количества использованных дней
  cert_app_prev_place: UploadFileType[] | undefined;
  // Справка с предыдущего места работы другого родителя о том,
  // что на дату увольнения доп.выходные дни не использованы или
  // использованы частично с указанием дат и общего количества использованных дней
  cert_other_parent_prev_place: UploadFileType[] | undefined;

  // В связи со сдачей крови и ее компонентов
  blood_donation_date: Date | null;
  absent_from_work: boolean;
  first_day_date: Date | null;
  second_day_date: Date | null;
  reference: UploadFileType[] | undefined;
  reference_number: string;
}

export interface VacationSchedulesProps extends VacationScheduleType {}

export type VacationScheduleVacationType = {
  id: number;
  periods: (VacationScheduleItemType & { id: number })[];
};

export type ChildVacationPeriod = {
  id: number;
  start: Scalars['DateTime'];
  end: Scalars['DateTime'];
  days: number;
};

export interface VacationSchedulesFormValuesProps
  extends Pick<
    VacationSchedulesProps,
    'position_id' | 'assigned_days' | 'unassigned_days'
  > {
  vacations: VacationScheduleVacationType[];
  vacationApps?: VacationApplicationType[];
  selectedDates: Date[];
}

export class VacationsUtils {
  static VacationAppStatus = {
    [VacationStatusesEnum.Approved]: 'Approved',
    [VacationStatusesEnum.Complete]: 'Complete',
    [VacationStatusesEnum.NotReviewed]: 'NotReviewed',
    [VacationStatusesEnum.Registration]: 'Registration',
    [VacationStatusesEnum.Rejected]: 'Rejected',
    [VacationStatusesEnum.SentForApproval]: 'SentForApproval'
  };

  static VacationAppStatusTitle = {
    [VacationStatusesEnum.Approved]: 'Согласовано',
    [VacationStatusesEnum.Complete]: 'Приказ издан',
    [VacationStatusesEnum.NotReviewed]: 'Не рассмотрено',
    [VacationStatusesEnum.Registration]: 'На оформлении в ОК',
    [VacationStatusesEnum.Rejected]: 'Отклонено',
    [VacationStatusesEnum.SentForApproval]: 'Отправлено на согласование'
  };

  static getSelectedDates(periods: { start: string; end: string }[]): Date[] {
    if (!periods?.length) return [];

    try {
      return flatten(
        periods
          .map(({ start, end }) => {
            if (!start || !end) return null;
            const startDate = new Date(getDateUtc(start));
            const endDate = new Date(getDateUtc(end));
            if (startDate.getTime() > endDate.getTime()) return null;
            return eachDayOfInterval({
              start: startDate,
              end: endDate
            });
          })
          .filter(Boolean)
      ) as Date[];
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  static toForm(): VacationAppProps {
    return {
      position_id: '',
      deputy_id: '',
      deputy_position_id: '',
      start: '',
      end: '',
      availableDaysAmount: null,
      selectedDaysAmount: null,
      isHeadPosition: false,
      vacation_type: '',
      no_pay_reason: '',
      no_pay_reason_text: '',
      invitation_note: undefined,
      digital_sick_leave_issued: false,
      sick_leave_number: '',
      sick_leave_doc: undefined,
      date_of_birth: null,
      birth_certificate: undefined,
      disability_certificate: undefined,
      disability_permanent: false,
      disability_start: null,
      disability_end: null,
      child_surname: '',
      child_name: '',
      child_patronymic: '',
      child_birth_date: null,
      child_snils: '',
      child_kinship_degree: '',
      disabled_child_vacation_periods: undefined,
      confirm_child_place: undefined,
      cert_other_parent_place: undefined,
      cert_app_prev_place: undefined,
      cert_other_parent_prev_place: undefined,
      blood_donation_date: null,
      absent_from_work: true,
      first_day_date: null,
      second_day_date: null,
      reference: undefined,
      reference_number: ''
    };
  }

  static fromForm(data: VacationAppProps): VacationApplicationInput {
    const vacation_type_data = { vacation_type_params: JSON.stringify({}) };
    const vacation_type = vacationTypeSelectOptions.find(
      (t) => t.id.toString() === data.vacation_type
    )?.name;

    if (Number(data.vacation_type) === VACATION_TYPE_WITHOUT_PAY) {
      const no_pay_reason =
        Number(data.no_pay_reason) === VACATION_REASON_WITHOUT_PAY_OTHER
          ? data.no_pay_reason_text
          : vacationReasonSelectOptions.find(
              (r) => r.id.toString() === data.no_pay_reason
            )?.name;
      vacation_type_data.vacation_type_params = JSON.stringify({
        no_pay_reason
      });
    }

    if (
      [VACATION_TYPE_STUDY_WITHOUT_PAY, VACATION_TYPE_STUDY_WITH_PAY].includes(
        Number(data.vacation_type)
      )
    ) {
      vacation_type_data.vacation_type_params = JSON.stringify({
        invitation_note: data.invitation_note
      });
    }

    if (Number(data.vacation_type) === VACATION_TYPE_PREGNANCY) {
      vacation_type_data.vacation_type_params = JSON.stringify({
        digital_sick_leave_issued: data.digital_sick_leave_issued,
        sick_leave_number: data.sick_leave_number,
        sick_leave_doc: data.sick_leave_doc
      });
    }

    if (Number(data.vacation_type) === VACATION_TYPE_CHILD_CARE) {
      vacation_type_data.vacation_type_params = JSON.stringify({
        date_of_birth: data.date_of_birth,
        birth_certificate: data.birth_certificate
      });
    }

    if (Number(data.vacation_type) === VACATION_TYPE_DISABLED_CHILD_CARE) {
      const childKinshipDegree = vacationChildKinshipDegreeSelectOptions.find(
        (t) => t.id.toString() === data.child_kinship_degree
      )?.name;

      vacation_type_data.vacation_type_params = JSON.stringify({
        birth_certificate: data.birth_certificate,
        disability_certificate: data.disability_certificate,
        disability_permanent: data.disability_permanent,
        disability_start: data.disability_start,
        disability_end: data.disability_end,
        confirm_child_place: data.confirm_child_place,
        cert_other_parent_place: data.cert_other_parent_place,
        cert_app_prev_place: data.cert_app_prev_place,
        cert_other_parent_prev_place: data.cert_other_parent_prev_place,

        disabled_child_vacation_periods: JSON.stringify(
          data.disabled_child_vacation_periods
        ),
        child_surname: data.child_surname,
        child_name: data.child_name,
        child_patronymic: data.child_patronymic,
        child_birth_date: data.child_birth_date,
        child_snils: data.child_snils,
        child_kinship_degree: childKinshipDegree
      });
    }

    let startDate = data.start;
    let endDate = data.end;

    if (Number(data.vacation_type) === VACATION_TYPE_BLOOD_DONATION) {
      vacation_type_data.vacation_type_params = JSON.stringify({
        blood_donation_date: data.blood_donation_date,
        absent_from_work: data.absent_from_work,
        first_day_date: data.first_day_date,
        second_day_date: data.second_day_date,
        reference: data.reference,
        reference_number: data.reference_number
      });
      startDate = `${data.first_day_date ?? ''}`;
      endDate = `${data.first_day_date ?? ''}`;
    }

    return {
      position_id: data.position_id ?? '',
      deputy_id: data.deputy_position_id || undefined,
      start: startDate ?? '',
      end: endDate ?? '',
      vacation_type,
      ...vacation_type_data
    };
  }

  static toSchedulesForm(
    data?: VacationSchedulesProps
  ): VacationSchedulesFormValuesProps {
    const vacations = (data?.items || []).map((item, iItem) => ({
      id: iItem,
      periods: [
        {
          ...item,
          id: 0,
          start: convertToUtcStr(item.start) || '',
          end: convertToUtcStr(item.end) || ''
        }
      ]
    }));

    const selectedDates = VacationsUtils.getSelectedDates(data?.items || []);

    return {
      position_id: data?.position.id?.toString() || '',
      unassigned_days: data?.unassigned_days || 0,
      assigned_days: data?.assigned_days || 0,

      // custom
      vacations: vacations.length
        ? vacations
        : [
            {
              id: 0,
              periods: [
                {
                  id: 0,
                  days: 0,
                  start: '',
                  end: '',
                  original_schedule_date: ''
                }
              ]
            }
          ],
      selectedDates
    };
  }

  static fromSchedulesForm(
    formData: VacationSchedulesFormValuesProps,
    initialData: VacationSchedulesFormValuesProps
  ): VacationScheduleInput | null {
    const formVacations = formData.vacations;
    const initVacations = initialData.vacations;
    const initVacationsIds = initVacations.map((v) => v.id);

    const newPlans = flatten(
      formVacations
        .filter((v) => !initVacationsIds.includes(v.id))
        .map((s) => s.periods.map((p) => pick(p, ['start', 'end'])))
    );

    const replacePlans = initVacations
      .map((initVac, iVac) => {
        const initPeriod = initVac.periods[0];
        const currVac = formVacations.find((fv) => fv.id === initVac.id);

        if (!currVac) {
          return {
            old_start: initPeriod.start,
            old_end: initPeriod.end,
            new_plans: [],
            original_schedule_date: initPeriod.original_schedule_date
          };
        } else {
          const currPeriods = currVac.periods.map((p) =>
            pick(p, ['start', 'end'])
          );

          if (
            currPeriods.length > 1 ||
            !isSameDay(
              new Date(currPeriods[0].start),
              new Date(initPeriod.start)
            ) ||
            !isSameDay(new Date(currPeriods[0].end), new Date(initPeriod.end))
          ) {
            return {
              old_start: initPeriod.start,
              old_end: initPeriod.end,
              new_plans: currPeriods,
              original_schedule_date: initPeriod.original_schedule_date
            };
          } else {
            return null;
          }
        }
      })
      .filter(Boolean) as VacationScheduleReplaceInput[];

    if (!newPlans.length && !replacePlans.length) return null;

    return {
      position_id: initialData.position_id,
      new_plans: newPlans.length ? newPlans : undefined,
      replace_plans: replacePlans.length ? replacePlans : undefined
    };
  }
}
